import type { ShowEndpointQueryParams } from "@circle-react/api/paywallCheckoutApi.types";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { getQueryParam } from "@circle-react/helpers/urlHelpers";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import type { PaywallPreviewCouponApplication } from "../../../../hooks/useCouponPreviewMutation";
import type { CheckoutFormValuesProps } from "../../../../types/CheckoutFormValuesProps";

export const useCouponForm = () => {
  const { getValues, setValue } = useFormContext<CheckoutFormValuesProps>();
  const {
    areCouponsEnabled,
    applyCouponCode,
    checkoutPreviewContext,
    couponPreviewMutation,
    previewMutation,
    paywall,
  } = usePaywallCheckoutContext();
  const [isInitialCouponApplied, setInitialCouponApplied] = useState(false);

  const applyCoupon = useCallback(() => {
    const formData = getValues();
    const couponCode = formData["coupon_code"];

    const couponPreviewParameters: ShowEndpointQueryParams = {
      paywall_id: paywall.id,
      coupon_code: couponCode,
      scenario: "apply_coupon",
    };

    couponPreviewMutation.mutate(couponPreviewParameters, {
      onSuccess: (data: PaywallPreviewCouponApplication) => {
        applyCouponCode({ data });
      },
    });
  }, [getValues, applyCouponCode, couponPreviewMutation.mutate, paywall.id]);

  useEffect(() => {
    const couponCode =
      getQueryParam("coupon_code") ||
      checkoutPreviewContext?.coupon_applied?.code;

    if (couponCode && !isInitialCouponApplied && previewMutation.isSuccess) {
      setInitialCouponApplied(true);
      setValue("coupon_code", couponCode);
      applyCoupon();
    }
  }, [
    isInitialCouponApplied,
    checkoutPreviewContext?.coupon_applied?.code,
    setValue,
    applyCoupon,
    previewMutation.isSuccess,
  ]);

  return {
    areCouponsEnabled,
    couponPreviewMutation,
    applyCoupon,
  };
};
